import { HttpErrorResponse } from '@angular/common/http';
import { createFeature, createReducer, on } from '@ngrx/store';
import { BuyingPowerDetails, MultiExanteResponse, OrderData } from '@zero/shared/util-models';
import { MultiOrderInstrument } from '../entities/multi-order-instrument';
import { TradingDayInfo } from '../entities/trading-day-info.entity';
import { UserExperienceData } from '../entities/user-experience-data.entity';
import { tradeActions, tradeApiActions } from './trade.actions';

export type LoadingState = 'INITIAL' | 'LOADING' | 'LOADED' | 'ERROR';

export interface TradeState {
  loadingState: LoadingState;
  holidays: string[];
  tradingDayInfo: TradingDayInfo;
  shouldDisplayMultiOrderConsent: boolean;
  multiOrderConsentConfirmed: boolean;
  multiOrderInstruments: MultiOrderInstrument[];
  buyingPower: BuyingPowerDetails;
  verifyingMultiOrder: boolean;
  verifyMultiOrderSuccess: boolean;
  sendingMultiOrder: boolean;
  sendingMultiOrderSuccess: boolean;
  lastAddedInstrument: MultiOrderInstrument;
  lastChangedInstrument: MultiOrderInstrument;
  loadingMultiOrderInstruments: boolean;
  multiOrderValidationError: string;
  loadingExante: boolean;
  exante: MultiExanteResponse;
  isOrderVerified: boolean;
  isCombinedOrderVerified: boolean;
  error: HttpErrorResponse | null;
  combinedOrder: OrderData[];
  isOrderPlaced: boolean;
  isPlacingOrder: boolean;
  knowledgeAndExperience: UserExperienceData;
  sendingKnowledgeAndExperience: boolean;
  sentKnowledgeAndExperienceSuccessfully: boolean;
}

export const initialState: TradeState = {
  loadingState: 'INITIAL',
  holidays: [],
  tradingDayInfo: null,
  shouldDisplayMultiOrderConsent: false,
  multiOrderConsentConfirmed: false,
  multiOrderInstruments: [],
  buyingPower: null,
  verifyingMultiOrder: false,
  verifyMultiOrderSuccess: false,
  sendingMultiOrder: false,
  sendingMultiOrderSuccess: false,
  lastAddedInstrument: null,
  lastChangedInstrument: null,
  loadingMultiOrderInstruments: false,
  multiOrderValidationError: null,
  loadingExante: false,
  exante: null,
  isOrderVerified: false,
  isCombinedOrderVerified: false,
  error: null,
  combinedOrder: null,
  isOrderPlaced: false,
  isPlacingOrder: false,
  knowledgeAndExperience: null,
  sendingKnowledgeAndExperience: false,
  sentKnowledgeAndExperienceSuccessfully: false,
};

export const tradeFeature = createFeature({
  name: 'trade',
  reducer: createReducer(
    initialState,
    on(tradeApiActions.loadHolidaysFromApi, (state): TradeState => ({ ...state, loadingState: 'LOADING' })),
    on(tradeApiActions.loadHolidaysSuccess, (state, { holidays }): TradeState => ({ ...state, loadingState: 'LOADED', holidays })),
    on(tradeApiActions.loadHolidaysFailure, (state): TradeState => ({ ...state, loadingState: 'ERROR' })),
    on(tradeApiActions.loadTradingDayInfoSuccess, (state, { tradingDayInfo }): TradeState => ({ ...state, tradingDayInfo })),
    on(tradeApiActions.loadTradingDayInfoFailure, (state): TradeState => ({ ...state, tradingDayInfo: null })),
    on(
      tradeApiActions.loadMultiOrderConsentSuccess,
      (state, { shouldDisplayConsent }): TradeState => ({ ...state, shouldDisplayMultiOrderConsent: shouldDisplayConsent }),
    ),
    on(tradeApiActions.loadMultiOrderConsentFailure, (state): TradeState => ({ ...state, shouldDisplayMultiOrderConsent: false })),
    on(
      tradeActions.addMultiOrderInstruments,
      (state, { instruments }): TradeState => ({ ...state, multiOrderInstruments: instruments, loadingMultiOrderInstruments: true }),
    ),
    on(tradeActions.addMultiOrderInstrument, (state, { instrument }): TradeState => {
      return { ...state, multiOrderInstruments: [...state.multiOrderInstruments, instrument], lastAddedInstrument: instrument };
    }),
    on(tradeActions.removeMultiOrderInstrument, (state, { instrument }): TradeState => {
      const multiOrderInstruments = state.multiOrderInstruments.filter(
        (i) => !(i.isin === instrument.isin && i.direction === instrument.direction),
      );
      return { ...state, multiOrderInstruments };
    }),
    on(
      tradeActions.removeMultiOrderInstruments,
      (state): TradeState => ({ ...state, multiOrderInstruments: [], verifyMultiOrderSuccess: false, sendingMultiOrderSuccess: false }),
    ),
    on(
      tradeActions.updateQuote,
      (state, { isin, quote }): TradeState => ({
        ...state,
        multiOrderInstruments: state.multiOrderInstruments.map((i) => {
          if (i.isin !== isin) {
            return i;
          }

          const quote2calc = i.direction === 'buy' ? quote?.ask : quote?.bid;
          return { ...i, quote, estimatedSum: i.qty ? i.qty * quote2calc : 0 };
        }),
      }),
    ),
    on(
      tradeActions.updateQuotes,
      (state, { quotes }): TradeState => ({
        ...state,
        multiOrderInstruments: state.multiOrderInstruments.map((i) => {
          const quote = quotes[i.isin];
          if (!quote) {
            return i;
          }

          const quote2calc = i.direction === 'buy' ? quote?.ask : quote?.bid;
          return { ...i, quote, estimatedSum: i.qty ? i.qty * quote2calc : 0 };
        }),
      }),
    ),
    on(
      tradeActions.setMultiOrderInstrumentError,
      (state, { instrument, error }): TradeState => ({
        ...state,
        multiOrderInstruments: state.multiOrderInstruments.map((i) => {
          if (i.isin !== instrument.isin) {
            return i;
          }

          return error ? { ...i, error, estimatedSum: 0 } : { ...i, error: null };
        }),
      }),
    ),
    on(tradeActions.subscribeToPushQuote, (state, { instrument }): TradeState => {
      return {
        ...state,
        multiOrderInstruments: state.multiOrderInstruments.map((i) => {
          if (i.isin !== instrument.isin) {
            return i;
          }

          return { ...i, error: null };
        }),
      };
    }),
    on(tradeActions.updateQuantity, (state, { instrument, quantity: qty }): TradeState => {
      const multiOrderInstruments = state.multiOrderInstruments.map((i) =>
        i.isin !== instrument.isin || i.direction !== instrument.direction
          ? i
          : { ...i, qty, estimatedSum: qty * (i.direction === 'buy' ? i.quote?.ask : i.quote?.bid) },
      );

      return {
        ...state,
        lastChangedInstrument: multiOrderInstruments.find((i) => i.isin === instrument.isin && i.direction === instrument.direction),
        multiOrderInstruments,
      };
    }),
    on(tradeApiActions.loadBuyingPowerSuccess, (state, { buyingPower }): TradeState => ({ ...state, buyingPower })),
    on(tradeActions.validateMultiOrder, (state): TradeState => ({ ...state, verifyingMultiOrder: true })),
    on(
      tradeApiActions.validateMultiOrderSuccess,
      (state): TradeState => ({ ...state, verifyingMultiOrder: false, verifyMultiOrderSuccess: true, multiOrderValidationError: null }),
    ),
    on(tradeApiActions.validateMultiOrderFailure, (state, { error }): TradeState => {
      const multiOrderInstruments = error.errors
        ? state.multiOrderInstruments.map((i) => {
            const e = error.errors.find(({ isin, direction }) => isin === i.isin && direction === i.direction);
            return e ? { ...i, error: e.message } : i;
          })
        : state.multiOrderInstruments;

      return {
        ...state,
        verifyingMultiOrder: false,
        verifyMultiOrderSuccess: false,
        multiOrderValidationError: error.message ?? 'Bad Request',
        multiOrderInstruments,
      };
    }),
    on(tradeActions.resetMultiOrderVerification, (state): TradeState => ({ ...state, verifyMultiOrderSuccess: false })),
    on(tradeActions.sendMultiOrder, (state): TradeState => ({ ...state, sendingMultiOrder: true })),
    on(
      tradeApiActions.sendMultiOrderSuccess,
      (state): TradeState => ({ ...state, sendingMultiOrder: false, sendingMultiOrderSuccess: true }),
    ),
    on(
      tradeApiActions.sendMultiOrderFailure,
      (state, { error }): TradeState => ({
        ...state,
        sendingMultiOrder: false,
        sendingMultiOrderSuccess: false,
        multiOrderValidationError: error.message ?? 'Bad Request',
      }),
    ),
    on(
      tradeActions.setMinimumQuantitySurcharge,
      (state, { instrument, value }): TradeState => ({
        ...state,
        multiOrderInstruments: state.multiOrderInstruments.map((i) =>
          i.isin !== instrument.isin || i.direction !== instrument.direction ? i : { ...i, minimumSurcharge: value },
        ),
      }),
    ),
    on(tradeActions.setMultiOrderInstrumentNotice, (state, { instrument, notice }): TradeState => {
      const multiOrderInstruments = state.multiOrderInstruments.map((i) =>
        i.isin !== instrument.isin || i.direction !== instrument.direction ? i : { ...i, notice },
      );

      return {
        ...state,
        lastChangedInstrument: multiOrderInstruments.find((i) => i.isin === instrument.isin && i.direction === instrument.direction),
        multiOrderInstruments,
      };
    }),
    on(tradeActions.clearMultiOrder, (): TradeState => initialState),
    on(tradeActions.confirmMultiOrderInstrumentNotice, (state, { instrument }): TradeState => {
      const multiOrderInstruments = state.multiOrderInstruments.map((i) =>
        i.isin !== instrument.isin || i.direction !== instrument.direction ? i : { ...i, notice: null },
      );

      return {
        ...state,
        lastChangedInstrument: multiOrderInstruments.find((i) => i.isin === instrument.isin && i.direction === instrument.direction),
        multiOrderInstruments,
      };
    }),
    on(tradeApiActions.confirmMultiOrderConsentSuccess, (state): TradeState => ({ ...state, multiOrderConsentConfirmed: true })),
    on(
      tradeApiActions.addMultiOrderInstrumentsSuccess,
      (state, { instruments }): TradeState => ({ ...state, multiOrderInstruments: instruments, loadingMultiOrderInstruments: false }),
    ),
    on(tradeApiActions.addMultiOrderInstrumentsFailure, (state): TradeState => ({ ...state, loadingMultiOrderInstruments: false })),
    on(tradeActions.loadExante, (state): TradeState => ({ ...state, loadingExante: true, exante: null })),
    on(
      tradeApiActions.loadExanteSuccess,
      (state, { multiExante }): TradeState => ({ ...state, loadingExante: false, exante: multiExante }),
    ),
    on(tradeApiActions.loadExanteFailure, (state): TradeState => ({ ...state, loadingExante: false })),
    on(
      tradeActions.verifyOrder,
      (state): TradeState => ({ ...state, error: null, isOrderVerified: false, isCombinedOrderVerified: false }),
    ),
    on(tradeApiActions.verifyOrderSuccess, (state): TradeState => ({ ...state, error: null, isOrderVerified: true })),
    on(tradeApiActions.verifyOrderFailure, (state, { error }): TradeState => ({ ...state, error, isOrderVerified: false })),
    on(
      tradeActions.validateCombinedOrder,
      (state): TradeState => ({ ...state, error: null, isOrderVerified: false, isCombinedOrderVerified: false }),
    ),
    on(
      tradeApiActions.validateCombinedOrderSuccess,
      (state, { orders }): TradeState => ({ ...state, error: null, isCombinedOrderVerified: true, combinedOrder: orders }),
    ),
    on(
      tradeApiActions.validateCombinedOrderFailure,
      (state, { error }): TradeState => ({ ...state, error, isCombinedOrderVerified: false }),
    ),
    on(
      tradeActions.removeFractionalOrder,
      (state): TradeState => ({ ...state, combinedOrder: [state.combinedOrder.find((order) => order.execType === 'market')] }),
    ),
    on(tradeActions.placeOrder, (state): TradeState => ({ ...state, error: null, isOrderPlaced: false, isPlacingOrder: true })),
    on(
      tradeApiActions.placeOrderSuccess,
      (state): TradeState => ({ ...state, error: null, isOrderVerified: false, isOrderPlaced: true, isPlacingOrder: false }),
    ),
    on(tradeApiActions.placeOrderFailure, (state, { error }): TradeState => ({ ...state, error, isPlacingOrder: false })),
    on(tradeActions.placeCombinedOrder, (state): TradeState => ({ ...state, error: null, isOrderPlaced: false, isPlacingOrder: true })),
    on(
      tradeApiActions.placeCombinedOrderSuccess,
      (state): TradeState => ({ ...state, error: null, isCombinedOrderVerified: false, isOrderPlaced: true, isPlacingOrder: false }),
    ),
    on(tradeApiActions.placeCombinedOrderFailure, (state, { error }): TradeState => ({ ...state, error, isPlacingOrder: false })),
    on(tradeActions.reset, (state): TradeState => ({ ...initialState, holidays: state.holidays })),
    on(
      tradeApiActions.loadKnowledgeAndExperienceFormSuccess,
      (state, { response }): TradeState => ({ ...state, knowledgeAndExperience: response }),
    ),
    on(tradeActions.submitKnowledgeAndExperienceForm, (state): TradeState => ({ ...state, sendingKnowledgeAndExperience: true })),
    on(
      tradeApiActions.submitKnowledgeAndExperienceFormSuccess,
      (state): TradeState => ({ ...state, sendingKnowledgeAndExperience: false, sentKnowledgeAndExperienceSuccessfully: true }),
    ),
    on(
      tradeApiActions.submitKnowledgeAndExperienceFormFailure,
      (state): TradeState => ({ ...state, sendingKnowledgeAndExperience: false, sentKnowledgeAndExperienceSuccessfully: false }),
    ),
  ),
});
